import { feathers } from '@/feathers-client';
import { HIDE_ERROR_AFTER_MS } from '@/Core/utilities/index';

const initialState = {
  inProgress: false,
  mediaError: false,
  mediaErrorMessage: '',
  videoDeleteSuccess: null,
  videoUpdateSuccess: null,
  pages: 0,
  skip: 0,
  totalItems: 0,
  videos: [],
  currentVideo: null,
  isRecording: false,
  recordedVideo: null,
  recordOptionType: null,
  recordedVideoTitle: null,
  recordedVideoDescription: null,
  recordedVideoTags: [],
};

export default {
  namespaced: true,
  state: { ...initialState },
  getters: {
    parsedVideoType(state) {
      if (state.recordOptionType) {
        const unparsedType = state.recordOptionType;
        return unparsedType.charAt(0).toUpperCase() + unparsedType.slice(1);
      }
    },
  },
  mutations: {
    setVideos(state, { videos, totalItems, pageSizeLimit, skip }) {
      state.videos = videos;
      state.totalItems = totalItems;
      state.pages = Math.ceil(totalItems / pageSizeLimit) || 0;
      state.skip = skip;
      state.mediaError = false;
    },
    setMediaError(state, error) {
      state.mediaError = true;
      if (error.error) {
        state.mediaError = error.error
      }
      state.inProgress = false;
      state.mediaErrorMessage = `error: ${error.message}`;
    },
    setInProgress(state, isInProgress) {
      state.inProgress = isInProgress;
    },
    addVideo(state, video) {
      state.currentVideo = video;
      state.videos = [...state.videos, video];
    },
    setCurrentVideo(state, video) {
      state.currentVideo = video;
    },
    updateVideo(state, modifiedVideo) {
      const updatedVideos = state.videos.map(videoIterator => {
        if (videoIterator.id === modifiedVideo.id) {
          videoIterator.title = modifiedVideo.title;
          videoIterator.description = modifiedVideo.description;
          videoIterator.tags = modifiedVideo.tags;
          videoIterator.tags_id = modifiedVideo.tags_id;
          return videoIterator;
        }
        return videoIterator;
      });
      state.videos = [...updatedVideos];
      state.videoUpdateSuccess = `Successfully updated ${modifiedVideo.title}`;
    },
    deleteVideo(state, deletedVideo) {
      state.videoDeleteSuccess = `Successfully deleted ${deletedVideo.title}`;
    },
    setRecordOptionType(state, type) {
      state.recordOptionType = type;
    },
    setRecordedVideo(state, blob) {
      state.recordedVideo = blob;
    },
    setRecordedVideoTitle(state, title) {
      state.recordedVideoTitle = title;
    },
    setRecordedVideoDescription(state, description) {
      state.recordedVideoDescription = description;
    },
    setRecordedVideoTags(state, tags) {
      state.recordedVideoTags = tags;
    },
    setIsRecording(state, isRecording) {
      state.isRecording = isRecording;
    },
  },
  actions: {
    async editVideo({ commit }, { video }) {
      const tagsArray = video.tags ? video.tags.map(tag => tag.content) : null;
      const formData = new FormData();
      formData.set('title', video.title);
      formData.set('description', video.description);
      formData.set('tags', tagsArray);

      try {
        await feathers.service('media').patch(video.id, formData);
        commit('updateVideo', video);
      } catch (error) {
        commit('setMediaError', error);
      }
    },
    async deleteVideo({ commit }, { video }) {
      try {
        await feathers.service('media').remove(video.id);
        commit('deleteVideo', video);
      } catch (error) {
        commit('setMediaError', error);
      }
    },
    async findVideos({ commit }, query = {}) {
      let response;
      commit('setInProgress', true);
      try {
        response = await feathers.service('media').find({ query });
        const payload = {
          videos: response.data,
          totalItems: response.total,
          pageSizeLimit: response.limit,
        };

        commit('setVideos', payload);
      } catch (error) {
        commit('setMediaError', error);
      }
      commit('setInProgress', false);
    },
    async pollVideos({ commit }, query = {}) {
      let response;
      try {
        response = await feathers.service('media').find({ query });
        const payload = {
          videos: response.data,
          totalItems: response.total,
          pageSizeLimit: response.limit,
        };
        commit('setVideos', payload);
      } catch (error) {
        commit('setMediaError', error);
        setTimeout(() => {
          commit('setMediaError', { error: false, message: '' });
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    async uploadVideos({ commit }, { files: videos, type }) {
      commit('setInProgress', true);
      await Promise.all(
        Object.values(videos).map(async video => {
          const formData = new FormData();
          formData.set('media', video);
          formData.set('title', video.name);
          formData.set('communication_type', type);
          formData.set('isRecorded', false);
          let response;
          try {
            response = await feathers.service('media').create(formData);
            commit('addVideo', response);
          } catch (error) {
            commit('setMediaError', error);
            commit('setCurrentVideo', null);
            setTimeout(() => {
              commit('setMediaError', { error: false, message: '' });
            }, HIDE_ERROR_AFTER_MS);
          }
        }),
      );
      commit('setInProgress', false);
    },
    async uploadRecordedVideo({ commit, state }, { video }) {
      commit('setInProgress', true);
      if (video) {
        const formData = new FormData();
        formData.set('media', new File([video], video.name, { type: video.type }));
        formData.set('title', state.recordedVideoTitle ? state.recordedVideoTitle : video.name);
        if (state.recordedVideoDescription) formData.set('description', state.recordedVideoDescription);
        if (state.recordedVideoTags) formData.set('tags', state.recordedVideoTags.map(tag => tag.content));
        formData.set('communication_type', state.recordOptionType);
        formData.set('isRecorded', true);
        let response;
        try {
          response = await feathers.service('media').create(formData);
          commit('addVideo', response);
        } catch (error) {
          commit('setMediaError', error);
          setTimeout(() => {
            commit('setMediaError', { error: false, message: '' });
          }, HIDE_ERROR_AFTER_MS);
        }
      } else {
        commit('setMediaError', { message: 'must record a video to upload.' });
      }
      commit('setInProgress', false);
    },
    async getVideo({ commit }, { id }) {
      commit('setInProgress', true);
      try {
        const response = await feathers.service('media').get(id);

        commit('setCurrentVideo', response);
      } catch (error) {
        commit('setMediaError', error);
        commit('setCurrentVideo', null);
      }
      commit('setInProgress', false);
    }
  },
};
